"use client";

import { useMemo, useRef, useState } from "react";

import CloseIcon from "@repo/ui-system/icons/action/close/outline.svg";
import MenuIcon from "@repo/ui-system/icons/action/hamburger-menu/outline.svg";

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { HomepageLogo } from "@/features/header/homepage-logo";
import { type BestQuickPicks } from "@/lib/api/best/quick-picks/schema";

import type { Category } from "../categories";
import { collectPanels, pathEquals, pathStartsWith } from "./mobile-panels";
import { MobileRootPanel } from "./mobile-root-panel";
import { MobileSeoNav } from "./mobile-seo-nav";
import { MobileSubPanel } from "./mobile-sub-panel";

type Props = {
  className?: string;
  quickPicksItems: BestQuickPicks | null;
  categories: Category[];
};

export function MainMenuMobile({
  className,
  quickPicksItems,
  categories,
}: Props) {
  const [activePath, setActivePath] = useState<number[]>([]);
  const rootPanelRef = useRef<HTMLDivElement>(null);
  const panelRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const wrappedCategories = useMemo<Category[]>(
    () => [
      { type: "leaf", name: "About Us", url: "/pages/about" },
      { type: "node", name: "Categories", children: categories },
    ],
    [categories],
  );

  const allPanels = useMemo(
    () => collectPanels(wrappedCategories),
    [wrappedCategories],
  );

  const drillDown = (panelPath: number[], childIndex: number) => {
    setActivePath([...panelPath, childIndex]);
  };

  const goBack = () => {
    const newPath = activePath.slice(0, -1);
    setActivePath(newPath);
    requestAnimationFrame(() => {
      if (newPath.length === 0) {
        rootPanelRef.current?.focus();
      } else {
        panelRefs.current.get(newPath.join("-"))?.focus();
      }
    });
  };

  const isRootActive = activePath.length === 0;

  return (
    <>
      <MobileSeoNav
        quickPicksItems={quickPicksItems}
        categories={categories}
      />
      <Drawer
        direction="left"
        onAnimationEnd={(open) => {
          if (!open) setActivePath([]);
        }}
      >
        <DrawerTrigger className={className}>
          <MenuIcon className="size-6" />
        </DrawerTrigger>
        <DrawerContent className="z-drawer max-w-none! min-w-[344px]">
          <DrawerHeader className="px-5 py-4 border-b bg-surface-grayscale-subtle">
            <DrawerTitle asChild>
              <div className="flex items-center justify-between">
                <HomepageLogo />
                <DrawerClose>
                  <CloseIcon className="size-6" />
                </DrawerClose>
                <DrawerDescription className="sr-only" />
              </div>
            </DrawerTitle>
          </DrawerHeader>
          <div className="relative flex-1 overflow-hidden">
            <MobileRootPanel
              isActive={isRootActive}
              ref={rootPanelRef}
              onDrillDown={(index) => drillDown([], index)}
              quickPicksItems={quickPicksItems}
              categories={wrappedCategories}
            />

            {allPanels.map((panel) => {
              const isActive = pathEquals(activePath, panel.path);
              const isPast =
                !isActive && pathStartsWith(activePath, panel.path);

              return (
                <MobileSubPanel
                  key={panel.path.join("-")}
                  ref={(el) => {
                    const key = panel.path.join("-");
                    if (el) panelRefs.current.set(key, el);
                    else panelRefs.current.delete(key);
                  }}
                  panel={panel}
                  isActive={isActive}
                  isPast={isPast}
                  onGoBack={goBack}
                  onDrillDown={(index) => drillDown(panel.path, index)}
                />
              );
            })}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
